import type { Dispatch, FormEvent, RefObject, SetStateAction } from "react";
import type { TextShortcut } from "../../types.ts";
import { normalizeShortcutTrigger } from "../Shortcuts.helpers.ts";
import { scopeLabel } from "./formatters.ts";
import { VARIABLE_TOKENS, type ShortcutFormScope, type ShortcutFormState } from "./types.ts";

type ShortcutEditorProps = {
  form: ShortcutFormState;
  editing: TextShortcut | null;
  bodyRef: RefObject<HTMLTextAreaElement>;
  isAdmin: boolean;
  saving: boolean;
  previewText: string;
  onFormChange: Dispatch<SetStateAction<ShortcutFormState>>;
  onInsertVariable: (token: string) => void;
  onNew: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
};

export function ShortcutEditor({
  form,
  editing,
  bodyRef,
  isAdmin,
  saving,
  previewText,
  onFormChange,
  onInsertVariable,
  onNew,
  onSubmit,
}: ShortcutEditorProps) {
  function patchForm(next: Partial<ShortcutFormState>) {
    onFormChange((prev) => ({ ...prev, ...next }));
  }

  return (
    <form className="shortcuts-editor" onSubmit={onSubmit}>
      <div className="shortcuts-editor-head">
        <div>
          <span className="shortcuts-kicker">{editing ? "Editando" : "Novo"}</span>
          <h2>{editing ? editing.trigger : "Criar atalho"}</h2>
        </div>
        {editing ? <span className={`shortcut-scope scope-${editing.scope}`}>{scopeLabel(editing.scope)}</span> : null}
      </div>

      <div className="shortcuts-form-row">
        <label>
          <span className="field-label">Gatilho</span>
          <input
            className="field"
            value={form.trigger}
            placeholder="/pix"
            maxLength={64}
            onChange={(event) => {
              const trigger = event.currentTarget.value;
              patchForm({ trigger });
            }}
            onBlur={() => onFormChange((prev) => ({ ...prev, trigger: normalizeShortcutTrigger(prev.trigger) }))}
          />
        </label>
        <label>
          <span className="field-label">Título</span>
          <input
            className="field"
            value={form.title}
            placeholder="Pagamento via Pix"
            maxLength={255}
            onChange={(event) => {
              const title = event.currentTarget.value;
              patchForm({ title });
            }}
          />
        </label>
        <label>
          <span className="field-label">Escopo</span>
          <select
            className="field"
            value={isAdmin ? form.scope : "personal"}
            disabled={!isAdmin}
            onChange={(event) => {
              const scope = event.currentTarget.value as ShortcutFormScope;
              patchForm({ scope });
            }}
          >
            <option value="personal">Meu</option>
            <option value="global">Todos</option>
          </select>
        </label>
      </div>

      <label className="shortcuts-body-field">
        <span className="field-label">Texto</span>
        <textarea
          ref={bodyRef}
          className="field"
          rows={13}
          value={form.body}
          placeholder="Escreva a mensagem. Use {{nome}}, {{telefone}}, {{curso}} ou {{valor}}..."
          maxLength={4000}
          onChange={(event) => {
            const body = event.currentTarget.value;
            patchForm({ body });
          }}
        />
      </label>

      <div className="shortcuts-vars" aria-label="Variaveis disponiveis">
        {VARIABLE_TOKENS.map((token) => (
          <button key={token} className="shortcut-token" type="button" onClick={() => onInsertVariable(token)}>
            {token}
          </button>
        ))}
      </div>

      <div className="shortcuts-preview">
        <span>Prévia com a conversa atual</span>
        <p>{previewText}</p>
      </div>

      <footer className="shortcuts-editor-actions">
        <button className="btn btn-secondary" type="button" onClick={onNew}>
          Limpar
        </button>
        <button className="btn btn-primary" type="submit" disabled={saving}>
          {saving ? "Salvando..." : "Salvar atalho"}
        </button>
      </footer>
    </form>
  );
}
